import { Transform, Type } from 'class-transformer';
import {
  IsBoolean,
  IsEnum,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUrl,
  IsUUID,
  Length,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator';

export class SupportRequestResponseDto {
  /**
   * Short, unique correlation id (e.g. `SUP-Xk9_2aQ1`) generated and persisted
   * on the request; included in the email subject so the support team can match
   * the user's mention of the id to the email thread and the stored request.
   */
  @IsString()
  ticketId: string;
}

export enum SupportRequestContactMethod {
  Email = 'email',
  Phone = 'phone',
}

/**
 * The workflow context the requester was viewing the case under when they
 * submitted the request — i.e. which access route the case link points at.
 * `clinic` (in-clinic review), `tele` (teledermatology), `pathology`
 * (pathological assessment), or `admin`. Lets support reproduce the user's
 * view by opening the same access type.
 */
export const SUPPORT_REQUEST_ACCESS_TYPES = [
  'clinic',
  'tele',
  'pathology',
  'admin',
] as const;
export type SupportRequestAccessType =
  (typeof SUPPORT_REQUEST_ACCESS_TYPES)[number];

export class SupportRequestCaseLinkDto {
  @IsUUID()
  healthRecordId: string;

  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsIn(SUPPORT_REQUEST_ACCESS_TYPES)
  accessType: SupportRequestAccessType;

  // Built client-side from the current location; restrict to http(s) so a
  // crafted payload can't embed e.g. a `javascript:` link in the support email.
  @IsString()
  @Length(1, 2048)
  @IsUrl({ protocols: ['http', 'https'], require_protocol: true })
  url: string;
}

export class SupportRequestDto {
  @IsString()
  @IsNotEmpty()
  @MaxLength(10000)
  message: string;

  @IsEnum(SupportRequestContactMethod)
  contactMethod: SupportRequestContactMethod;

  @IsString()
  @Length(1, 320)
  contactValue: string;

  @IsOptional()
  @ValidateNested()
  @Type(() => SupportRequestCaseLinkDto)
  caseLink?: SupportRequestCaseLinkDto;
}

export class SupportRequestAdminSearchQueryDto {
  @IsNumber()
  @Min(0)
  skip: number;

  @IsNumber()
  @Min(1)
  take: number;

  /** Matches ticket id, requester name/email, or medical unit name. */
  @IsOptional()
  @IsString()
  searchText?: string;

  /** Omitted = all, true = completed only, false = open only. */
  @IsOptional()
  @IsBoolean()
  @Transform(({ obj, key }) =>
    obj[key] === undefined ? undefined : obj[key] === 'true',
  )
  completed?: boolean;
}

export class UpdateSupportRequestCompletedDto {
  @IsBoolean()
  completed: boolean;
}

/**
 * Support request as shown in the admin app. Flat projection — the message
 * is only ever exposed here, never in email.
 */
export interface SupportRequestAdminDto {
  id: string;
  created: string | Date;
  ticketId: string;
  completed: boolean;
  message: string;
  contactMethod: SupportRequestContactMethod;
  contactValue: string;
  caseUrl?: string;
  caseAccessType?: SupportRequestAccessType;
  caseHealthRecordId?: string;
  caseConditionId?: string;
  requesterId?: string;
  requesterName?: string;
  requesterEmail?: string;
  /** Comma-separated snapshot of the requester's roles at submit time. */
  requesterRoles?: string;
  medicalUnitId?: string;
  medicalUnitName?: string;
  jurisdictionalGroupId?: string;
  jurisdictionalGroupName?: string;
  federationId?: string;
  federationName?: string;
}
